import { useMemo } from 'react';
import { format as formatDateFns, parse as parseDateFns } from 'date-fns';
import styled from 'styled-components';

import { CalendarIcon } from '@assets/icons/CalendarIcon';
import { LocationIcon } from '@assets/icons/LocationIcon';
import { RateIcon } from '@assets/icons/RateIcon';
import { RoleTypeIcon } from '@assets/icons/RoleTypeIcon';
import { UK_COUNTRY } from '@lib/utils/constants';
import {
  UK_ROLE_TYPE_VALUE_TO_LABEL,
  US_ROLE_TYPE_VALUE_TO_LABEL,
} from '@lib/utils/constants/applicationConstants';

import { RateItem } from '../../JobsBoardPost/RateItem';

type PostMainInfoProps = {
  post: JobsBoardApplication;
  hasSplitView?: boolean;
};
export const PostMainInfo = ({ post, hasSplitView }: PostMainInfoProps) => {
  const applicationDatesDisplay = useMemo(() => {
    return getApplicationDatesDisplay(
      post?.application_dates,
      post?.project_location?.country
    );
  }, [post?.application_dates, post?.project_location?.country]);

  const locationDisplay = useMemo(() => {
    const locationArea = post?.area;
    if (!locationArea) {
      return AREA_NOT_SPECIFIED;
    }
    return locationArea;
  }, [post?.area]);

  const roleTypeLabel = useMemo(() => {
    if (post?.project_location?.country === UK_COUNTRY) {
      return UK_ROLE_TYPE_VALUE_TO_LABEL[post?.role?.type] || '-';
    }
    return US_ROLE_TYPE_VALUE_TO_LABEL[post?.role?.type] || '-';
  }, [post?.project_location?.country, post?.role?.type]);

  const itemsWithIcons = [
    {
      id: 'application-dates',
      icon: <CalendarIcon />,
      infoText: applicationDatesDisplay,
    },
    {
      id: 'location',
      icon: <LocationIcon />,
      infoText: locationDisplay,
    },
    {
      id: 'rate',
      icon: <RateIcon />,
      infoText: (
        <RateItem
          unionStatus={post?.union_status}
          unionRate={post?.union_rate}
          nonUnionRate={post?.rate}
          projectLocationCountry={post?.project_location?.country}
          ukRateNote={post?.uk_rate_note}
          ukRateType={post?.uk_rate_type}
          ukRateTypeOther={post?.uk_rate_type_other}
        />
      ),
    },
    {
      id: 'role-type',
      icon: <RoleTypeIcon />,
      infoText: roleTypeLabel,
    },
  ];
  return (
    <StyledContainer hasSplitView={hasSplitView}>
      {itemsWithIcons.map((item) => {
        const itemInfoTextClassName = [
          'item-info-text',
          ...(item.infoText === AREA_NOT_SPECIFIED
            ? ['item-info-text-disabled']
            : []),
          ...(item.id === 'location' ? ['text-wrap-anywhere'] : []),
        ].join(' ');
        return (
          <div className='item-info' key={`item-with-icon-${item.id}`}>
            <div className='item-info-icon'>{item.icon}</div>
            <div className={itemInfoTextClassName}>{item.infoText}</div>
          </div>
        );
      })}
    </StyledContainer>
  );
};

const getApplicationDatesDisplay = (
  dates: ListApplicationDatesWithUnitsModel[],
  projectCountry: string | undefined
) => {
  const finalDateFormat = projectCountry === UK_COUNTRY ? 'dd/M' : 'M/dd';

  if (!dates?.length) {
    return '-';
  }

  return dates
    ?.map((date) => {
      const datesString = date?.dates
        ?.map((date) => {
          return formatDateFns(
            parseDateFns(date, 'yyyy-MM-dd', new Date()),
            finalDateFormat
          );
        })
        ?.join(', ');

      if (date?.unit_name === 'Main') {
        return `Work ${datesString || '-'}`;
      }

      return `${date?.unit_name} ${datesString || '-'}`;
    })
    .join(', ');
};

type StyledContainerProps = {
  hasSplitView?: boolean;
};
export const StyledContainer = styled.div<StyledContainerProps>`
  line-height: 20px;
  font-size: 14px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: ${({ hasSplitView }) => (hasSplitView ? 'calc(50% - 16px)' : '100%')};

  .item-info {
    display: flex;
    gap: 8px;
    align-items: flex-start;

    .item-info-icon {
      width: 20px;
      height: 20px;
      display: flex;
      align-items: center;
      justify-content: center;
    }

    .item-info-text {
      width: calc(100% - 28px);
      &.item-info-text-disabled {
        color: ${({ theme }) => theme.palette.text[10]};
      }
    }
  }

  @media (max-width: 768px) {
    width: 100%;
    font-size: 16px;
    line-height: 24px;
    .item-info {
      .item-info-icon {
        height: 24px;
      }
    }
  }
`;

const AREA_NOT_SPECIFIED = 'Filming area not specified';
